import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

export type BoqRevisionStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'superseded';

export interface Boq {
  id: number; code: string; omsId: number; title: string; description: string | null; currentRevisionId: number | null; createdAt: string;
}
export interface BoqRevision {
  id: number; boqId: number; revisionNo: number; status: BoqRevisionStatus; isCurrent: boolean;
  totalClientAmount: number; totalTax: number; totalClientValue: number;
  totalExecutionAmount?: number; totalProfit?: number; marginPct?: number;
  createdAt: string;
}
export interface BoqItem {
  id?: number; categoryId?: number | null; subCategoryId?: number | null; description: string; unit: string;
  quantity: number; clientUnitRate: number; executionUnitRate?: number; taxPercent: number;
  clientAmount?: number; clientTaxAmount?: number; clientTotalAmount?: number; lineProfit?: number; remarks?: string | null;
}
export interface BoqItemInput {
  categoryId?: number | null; subCategoryId?: number | null; description: string; unit: string;
  quantity: number; clientUnitRate: number; executionUnitRate: number; taxPercent: number; remarks?: string;
}
export interface CreateBoqInput { omsId: number; title: string; description?: string; templateId?: number; }
export interface BoqDashboard { totalBoqs: number; totalClientValue: number; totalProfit?: number; marginPct?: number; }

const base = createCrudApi<Boq, CreateBoqInput, { title?: string; description?: string }>('boqs');

export const boqApi = {
  ...base,
  async dashboard(): Promise<BoqDashboard> {
    const { data } = await apiClient.get<ApiSuccess<BoqDashboard>>('/boqs/dashboard');
    return data.data;
  },
  async getRevision(id: number, revisionId: number): Promise<BoqRevision & { items: BoqItem[] }> {
    const { data } = await apiClient.get<ApiSuccess<BoqRevision & { items: BoqItem[] }>>(`/boqs/${id}/revisions/${revisionId}`);
    return data.data;
  },
  async setItems(id: number, revisionId: number, items: BoqItemInput[]) {
    const { data } = await apiClient.put<ApiSuccess<BoqRevision & { items: BoqItem[] }>>(`/boqs/${id}/revisions/${revisionId}/items`, { items });
    return data.data;
  },
  async createRevision(id: number) {
    const { data } = await apiClient.post<ApiSuccess<BoqRevision & { items: BoqItem[] }>>(`/boqs/${id}/revisions`, {});
    return data.data;
  },
  async changeStatus(id: number, revisionId: number, action: 'submit' | 'approve' | 'reject', remarks?: string) {
    const { data } = await apiClient.post<ApiSuccess<BoqRevision>>(`/boqs/${id}/revisions/${revisionId}/status`, { action, remarks });
    return data.data;
  },
  async compare(id: number, from: number, to: number) {
    const { data } = await apiClient.get<ApiSuccess<{ added: BoqItem[]; removed: BoqItem[]; changed: Record<string, unknown>[] }>>(`/boqs/${id}/compare`, { params: { from, to } });
    return data.data;
  },
  async importCsv(id: number, revisionId: number, csv: string) {
    const { data } = await apiClient.post<ApiSuccess<BoqRevision & { items: BoqItem[] }>>(`/boqs/${id}/revisions/${revisionId}/import`, { csv });
    return data.data;
  },
  exportUrl: (id: number, revisionId: number) => `/boqs/${id}/revisions/${revisionId}/export`,
};
